import { IssueNotificationEventItem, Permission } from '@cims-online/shared'
import { EditNotificationEventOptionsFn } from 'components/CIMSNotifications/types'
import { useCallback } from 'react'
import { useUpsertIssueNotificationMutation } from 'schema'
import { useIssueNotificationsContext } from '../../IssueNotificationsContext'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { useRecipientsSelectContext } from 'components/CIMSSelect/common/RecipientsSelect/RecipientsSelectContext'
import RenderFormItems from './components/RenderFormItems'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { TypeItem } from './components/RenderFormItems/TypeFormItems/types'
import { useProjectContext } from 'context/ProjectContext'

export function useEdit() {
  const { projectId } = useProjectContext()
  const { recipients, refetch } = useIssueNotificationsContext()
  const { toLabeledValues, fromLabeledValues } = useRecipientsSelectContext()
  const [edit] = useUpsertIssueNotificationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      upsertIssueNotification: {
        notification: { notificationEvent },
      },
    }) => simpleEditNotification(notificationEvent),
  })
  return useCallback<
    EditNotificationEventOptionsFn<
      { every: LabeledValueType[]; types: TypeItem[] },
      IssueNotificationEventItem
    >
  >(
    (event) => {
      const { every, byIssueType } = recipients(event)
      return {
        permission: Permission.EditIssueNotification,
        initialValues: {
          every: toLabeledValues(every),
          types: (byIssueType ?? []).map(({ type, recipients }) => ({
            typeId: type.id,
            recipients: toLabeledValues(recipients),
          })),
        },
        onClear: async () => {
          await edit({
            variables: {
              input: {
                every: { userIds: [], nonUserEmailIds: [] },
                byIssueType: [],
                notificationEventId: event.id,
                projectId,
              },
            },
          })
        },
        onSave: async ({ every, types }) => {
          await edit({
            variables: {
              input: {
                projectId,
                notificationEventId: event.id,
                byIssueType: types.map(({ typeId, recipients }) => ({
                  typeId,
                  recipients: fromLabeledValues(recipients),
                })),
                every: fromLabeledValues(every),
              },
            },
          })
          refetch()
        },
        render: <RenderFormItems />,
      }
    },
    [edit, fromLabeledValues, projectId, recipients, refetch, toLabeledValues]
  )
}
